import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Loader2,
  Plus,
  Pencil,
  Star,
  Trash2,
  ExternalLink,
  MoreHorizontal,
  Store,
  Link2,
  MapPin,
  CheckCircle2,
} from "lucide-react";
import { PageHeader } from "@/business/components/PageHeader";
import VenueForm from "@/business/components/VenueForm";
import {
  listMyLocations,
  createLocation,
  updateLocation,
  setPrimaryLocation,
  updateLocationSlug,
  deleteLocation,
  BUSINESS_TYPE_OPTIONS,
} from "@/business/api";

function typeLabel(t) {
  return (BUSINESS_TYPE_OPTIONS.find((o) => o.value === t) || { label: "Venue" }).label;
}

function VenueRow({
  venue,
  busy,
  onEdit,
  onSetPrimary,
  onChangeSlug,
  onDelete,
}) {
  const slug = venue.slug || venue.username || "";
  const publicHref = slug ? `/business/${encodeURIComponent(slug)}` : null;
  const initials = (venue.business_name || "V").slice(0, 2).toUpperCase();
  const isPrimary = !!venue.is_primary;

  return (
    <div
      className="flex items-start sm:items-center gap-4 p-4 sm:p-5 transition-colors hover:bg-[#FAFAFB]"
      data-testid={`venue-row-${venue.id}`}
    >
      {/* Photo */}
      <div className="shrink-0">
        {venue.profile_photo ? (
          <img
            src={venue.profile_photo}
            alt=""
            className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl object-cover border border-[#E5E7EB]"
          />
        ) : (
          <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl bg-[#FFF1E8] text-[#FF6A00] border border-[#FFD7BF] flex items-center justify-center text-base font-semibold">
            {initials}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="text-sm sm:text-base font-semibold text-[#111827] truncate"
            data-testid={`venue-row-name-${venue.id}`}
          >
            {venue.business_name || "Untitled venue"}
          </div>
          <Badge
            variant="secondary"
            className="bg-[#F0F2F5] text-[#374151] border-0 text-[10px] uppercase tracking-wide"
            data-testid={`venue-row-type-${venue.id}`}
          >
            {typeLabel(venue.business_type)}
          </Badge>
          {isPrimary && (
            <Badge
              className="bg-[#FFF1E8] text-[#B54708] border border-[#FFD7BF] text-[10px] uppercase tracking-wide"
              data-testid={`venue-row-primary-badge-${venue.id}`}
            >
              <CheckCircle2 className="h-3 w-3 mr-1" /> Primary
            </Badge>
          )}
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#6B7280]">
          {publicHref ? (
            <a
              href={publicHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[#FF6A00] hover:underline"
              data-testid={`venue-row-slug-link-${venue.id}`}
            >
              <Link2 className="h-3 w-3" />
              /business/{slug}
              <ExternalLink className="h-3 w-3 opacity-70" />
            </a>
          ) : (
            <span className="inline-flex items-center gap-1 text-[#9CA3AF]">
              <Link2 className="h-3 w-3" /> No public slug yet
            </span>
          )}
          {venue.city && (
            <span
              className="inline-flex items-center gap-1"
              data-testid={`venue-row-city-${venue.id}`}
            >
              <MapPin className="h-3 w-3" /> {venue.city}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onEdit(venue)}
          disabled={busy}
          className="hidden sm:inline-flex"
          data-testid={`venue-row-edit-button-${venue.id}`}
        >
          <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              disabled={busy}
              aria-label="More actions"
              data-testid={`venue-row-actions-trigger-${venue.id}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem
              onSelect={(e) => {
                // Radix focus-restoration can cancel a same-tick Dialog open.
                // Defer the parent state update so the menu closes first.
                e.preventDefault();
                setTimeout(() => onEdit(venue), 0);
              }}
              data-testid={`venue-row-menu-edit-${venue.id}`}
            >
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setTimeout(() => onChangeSlug(venue), 0);
              }}
              data-testid={`venue-row-menu-change-slug-${venue.id}`}
            >
              <Link2 className="h-4 w-4 mr-2" /> Change slug
            </DropdownMenuItem>
            {!isPrimary && (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setTimeout(() => onSetPrimary(venue), 0);
                }}
                data-testid={`venue-row-menu-set-primary-${venue.id}`}
              >
                <Star className="h-4 w-4 mr-2" /> Set as primary
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-[#B42318] focus:text-[#B42318] focus:bg-[#FEE4E2]"
              onSelect={(e) => {
                e.preventDefault();
                setTimeout(() => onDelete(venue), 0);
              }}
              data-testid={`venue-row-menu-delete-${venue.id}`}
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export default function Venues() {
  const { user } = useOutletContext();
  const navigate = useNavigate();

  const [venues, setVenues] = useState(null); // null = loading
  const [loadError, setLoadError] = useState("");
  const [busyId, setBusyId] = useState(null);

  // Create / edit form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // venue object or null (= create)
  const [submitting, setSubmitting] = useState(false);

  // Set primary confirm
  const [primaryTarget, setPrimaryTarget] = useState(null);

  // Slug rename dialog
  const [slugTarget, setSlugTarget] = useState(null);
  const [slugDraft, setSlugDraft] = useState("");
  const [slugSubmitting, setSlugSubmitting] = useState(false);
  const [slugError, setSlugError] = useState("");

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const ownerCountry = user?.country || venues?.find((v) => v.is_primary)?.country || "";

  const refresh = async () => {
    try {
      const list = await listMyLocations();
      // Backend returns { total, items: [...] } — also tolerate
      // plain array or { locations: [...] } in case the contract changes.
      const arr = Array.isArray(list)
        ? list
        : list?.items || list?.locations || [];
      setVenues(arr);
      setLoadError("");
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        navigate("/business/login", { replace: true });
        return;
      }
      // Backend endpoints not yet deployed → show a clear inline message
      // so the owner doesn't think their data is gone.
      if (e.status === 404) {
        setLoadError(
          "Multi-venue management isn't available yet on this server. Please try again shortly."
        );
      } else {
        toast.error(e.message || "Could not load your venues.");
      }
      setVenues([]);
    }
  };

  useEffect(() => {
    // Seed from /me payload if present, then refresh in the background to
    // pick up any out-of-band updates.
    if (Array.isArray(user?.locations) && user.locations.length) {
      setVenues(user.locations);
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (venue) => {
    setEditing(venue);
    setFormOpen(true);
  };
  const closeForm = () => {
    if (submitting) return;
    setFormOpen(false);
    setEditing(null);
  };

  const onFormSubmit = async (payload) => {
    setSubmitting(true);
    try {
      if (editing) {
        const updated = await updateLocation(editing.id, payload);
        toast.success("Venue updated.");
        setVenues((arr) =>
          (arr || []).map((v) => (v.id === editing.id ? { ...v, ...(updated || payload), id: editing.id } : v))
        );
      } else {
        const created = await createLocation(payload);
        toast.success("Venue added.");
        setVenues((arr) => [...(arr || []), created || { ...payload, id: `tmp-${Date.now()}` }]);
      }
      setFormOpen(false);
      setEditing(null);
      // Refresh in background so we get authoritative slug / is_primary back.
      refresh();
    } catch (e) {
      // Re-throw so the form can show the error inline.
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Set Primary -----
  const confirmSetPrimary = async () => {
    if (!primaryTarget) return;
    setBusyId(primaryTarget.id);
    try {
      await setPrimaryLocation(primaryTarget.id);
      toast.success(`${primaryTarget.business_name || "Venue"} is now your primary venue.`);
      setPrimaryTarget(null);
      await refresh();
    } catch (e) {
      toast.error(e.message || "Could not set primary venue.");
    } finally {
      setBusyId(null);
    }
  };

  // ----- Change Slug -----
  const openSlugEditor = (venue) => {
    setSlugTarget(venue);
    setSlugDraft(venue.slug || "");
    setSlugError("");
  };
  const submitSlug = async (e) => {
    e?.preventDefault?.();
    if (!slugTarget) return;
    const next = (slugDraft || "").trim().toLowerCase();
    if (!next) {
      setSlugError("Slug can't be empty.");
      return;
    }
    if (!/^[a-z0-9-]+$/.test(next)) {
      setSlugError("Use lowercase letters, numbers and dashes only.");
      return;
    }
    setSlugError("");
    setSlugSubmitting(true);
    try {
      const updated = await updateLocationSlug(slugTarget.id, next);
      toast.success("Slug updated.");
      setVenues((arr) =>
        (arr || []).map((v) =>
          v.id === slugTarget.id ? { ...v, ...(updated || {}), slug: next } : v
        )
      );
      setSlugTarget(null);
      refresh();
    } catch (e) {
      // Backend says "already taken" / similar — surface verbatim inline.
      setSlugError(e.message || "Could not update slug.");
    } finally {
      setSlugSubmitting(false);
    }
  };

  // ----- Delete -----
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteLocation(deleteTarget.id);
      toast.success("Venue deleted.");
      setVenues((arr) => (arr || []).filter((v) => v.id !== deleteTarget.id));
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      // Backend message e.g. "Cannot delete your only venue" — verbatim toast.
      toast.error(e.message || "Could not delete venue.");
    } finally {
      setDeleting(false);
    }
  };

  const total = venues?.length ?? 0;
  const headerDesc = useMemo(() => {
    if (venues === null) return "Loading your venues…";
    if (total === 0) return "Add your first venue to get listed on BuskerBeat.";
    return `${total} venue${total === 1 ? "" : "s"} • Your primary venue is used as the default when posting gigs.`;
  }, [venues, total]);

  return (
    <div className="space-y-6" data-testid="venues-page">
      <PageHeader
        title="My Venues"
        description={headerDesc}
        action={
          <Button
            onClick={openCreate}
            className="bg-[#FF6A00] hover:bg-[#E85F00] text-white"
            data-testid="venues-add-button"
          >
            <Plus className="mr-2 h-4 w-4" /> Add venue
          </Button>
        }
      />

      <Card className="border-[#E5E7EB] shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {venues === null ? (
            <div
              className="flex items-center justify-center py-16"
              data-testid="venues-loading"
            >
              <Loader2 className="h-5 w-5 animate-spin text-[#FF6A00]" />
            </div>
          ) : venues.length === 0 ? (
            <div className="px-6 py-12 text-center" data-testid="venues-empty">
              <div className="mx-auto h-12 w-12 rounded-2xl bg-[#FFF1E8] text-[#FF6A00] flex items-center justify-center mb-3">
                <Store className="h-6 w-6" />
              </div>
              <div className="text-base font-semibold text-[#111827]">
                {loadError ? "Multi-venue not available yet" : "No venues yet"}
              </div>
              <div className="text-sm text-[#6B7280] mt-1 mb-4 max-w-md mx-auto">
                {loadError ||
                  "Add your first venue so fans and buskers can find you."}
              </div>
              {!loadError && (
                <Button
                  onClick={openCreate}
                  className="bg-[#FF6A00] hover:bg-[#E85F00] text-white"
                  data-testid="venues-empty-add-button"
                >
                  <Plus className="mr-2 h-4 w-4" /> Add your first venue
                </Button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-[#E5E7EB]" data-testid="venues-list">
              {venues.map((v) => (
                <li key={v.id}>
                  <VenueRow
                    venue={v}
                    busy={busyId === v.id}
                    onEdit={openEdit}
                    onSetPrimary={(venue) => setPrimaryTarget(venue)}
                    onChangeSlug={openSlugEditor}
                    onDelete={(venue) => setDeleteTarget(venue)}
                  />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={(o) => (o ? setFormOpen(true) : closeForm())}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="venue-form-dialog"
        >
          <DialogHeader>
            <DialogTitle data-testid="venue-form-dialog-title">
              {editing ? "Edit venue" : "Add a new venue"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "Update this venue's profile, photos and hours."
                : "Create a new location under your business. You can mark it primary later."}
            </DialogDescription>
          </DialogHeader>
          <VenueForm
            initial={editing}
            defaultCountry={ownerCountry}
            submitting={submitting}
            submitLabel={editing ? "Save changes" : "Create venue"}
            onSubmit={onFormSubmit}
            onCancel={closeForm}
          />
        </DialogContent>
      </Dialog>

      {/* Set primary confirm */}
      <AlertDialog
        open={!!primaryTarget}
        onOpenChange={(o) => !o && setPrimaryTarget(null)}
      >
        <AlertDialogContent data-testid="venue-set-primary-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Set as primary venue?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-[#111827]">
                {primaryTarget?.business_name}
              </span>{" "}
              will become your default venue. New gigs will default to this venue's
              location and contact details.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="venue-set-primary-cancel-button">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmSetPrimary}
              className="bg-[#FF6A00] hover:bg-[#E85F00] text-white"
              data-testid="venue-set-primary-confirm-button"
            >
              Make primary
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change slug dialog */}
      <Dialog
        open={!!slugTarget}
        onOpenChange={(o) => !o && !slugSubmitting && setSlugTarget(null)}
      >
        <DialogContent data-testid="venue-slug-dialog">
          <form onSubmit={submitSlug}>
            <DialogHeader>
              <DialogTitle>Change public slug</DialogTitle>
              <DialogDescription>
                This is the URL your venue appears under at{" "}
                <span className="font-mono text-[#111827]">buskerbeat.com/business/</span>
                <span className="font-mono text-[#FF6A00]">{slugDraft || "your-slug"}</span>
              </DialogDescription>
            </DialogHeader>
            <div className="py-3">
              <Input
                autoFocus
                value={slugDraft}
                onChange={(e) => setSlugDraft(e.target.value.replace(/\s+/g, "-"))}
                placeholder="e.g. sydney-jazz-bondi"
                data-testid="venue-slug-input"
              />
              {slugError && (
                <div
                  className="mt-2 text-sm text-[#DC2626]"
                  role="alert"
                  data-testid="venue-slug-error-text"
                >
                  {slugError}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setSlugTarget(null)}
                disabled={slugSubmitting}
                data-testid="venue-slug-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={slugSubmitting}
                className="bg-[#FF6A00] hover:bg-[#E85F00] text-white"
                data-testid="venue-slug-save-button"
              >
                {slugSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save slug
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && !deleting && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="venue-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this venue?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-[#111827]">
                {deleteTarget?.business_name}
              </span>{" "}
              will be permanently removed. Any gigs already posted will keep their
              existing details, but this venue's public page will no longer be
              reachable.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              data-testid="venue-delete-cancel-button"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={deleting}
              className="bg-[#B42318] hover:bg-[#912018] text-white"
              data-testid="venue-delete-confirm-button"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete venue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
